import React, { useEffect, useState } from "react";

import { useHistory, useParams } from "react-router-dom";

import SolicitationEdit from "../../../../../../presentation/pages/admin/solicitation/edit";

import {
  taxTypeList,
  especialTaxTypeList,
  planStatusList,
  addressTypeList,
} from "../../../../../../utils";

import {
  handleCepChange,
  getErrorMessage,
  makeCompany,
  makeCertificate,
  hasCertificate,
  showSuccessMessage,
  showErrorMessage,
  handleCnpjChange,
} from "../../../../../../presentation/helpers";

import {
  IPackage,
  ISolicitation,
} from "../../../../../../presentation/interfaces";
import { CompanyProps } from "../../../../../../domain/models/company.model";

import { makeRemotePackage } from "../../../../usecases/package/remote-package-factory";
import { makeRemoteSolicitation } from "../../../../usecases/solicitation/remote-solicitation-factory";
import { makeRemoteDeveloperPackage } from "../../../../usecases/developer-package/remote-developer-package-factory";
import { makeRemoteCompanies } from "../../../../usecases/companies/remote-companies-factory";
import { makeRemoteDeveloperCompanies } from "../../../../usecases/developer-companies/remote-developer-companies-factory";

import { settings } from "../../../../../configs/settings";

const MakeSolicitationEdit: React.FC = () => {
  const isSmallScreen = window.innerWidth < 768;

  const history = useHistory();

  const { id } = useParams<{ id: string }>();

  const [packages, setPackages] = useState<IPackage[]>([]);

  const [solicitation, setSolicitation] = useState<ISolicitation>();

  const [errorMessage, setErrorMessage] = useState<string | null>("");

  useEffect(() => {
    const fetchPackages = async (): Promise<void> => {
      const remotePackage = makeRemotePackage();
      try {
        const response = await remotePackage.loadAll();
        setPackages(response);
      } catch (error) {
        console.error("Error fetching packages:", error);
      }
    };

    fetchPackages();
  }, []);

  useEffect(() => {
    const fetchSolicitation = async (): Promise<void> => {
      const remoteSolicitation = makeRemoteSolicitation();
      try {
        const response = await remoteSolicitation.loadById(Number(id));
        setSolicitation(response);
      } catch (error) {
        console.error("Error fetching solicitation:", error);
      }
    };
    fetchSolicitation();
  }, [id]);

  const updatePlan = async (values: any): Promise<void> => {
    const developerPackageRemote = makeRemoteDeveloperPackage();

    await developerPackageRemote.update(
      solicitation?.developerPackage?.id || 0,
      {
        status: values?.planStatus || "",
      },
    );
  };

  const makeCompanyToUpdate = (values: any): any => {
    const { address, ...rest } = makeCompany(values);

    return {
      id: solicitation?.developerCompany?.company?.id,
      address: {
        id: solicitation?.developerCompany?.company?.address?.id,
        ...address,
      },
      ...rest,
    };
  };

  const updateCompany = async (values: any): Promise<void> => {
    await makeRemoteCompanies().update(
      solicitation?.developerCompany?.company?.id,
      makeCompanyToUpdate(values),
    );
  };

  const createCompany = async (values: any): Promise<CompanyProps> =>
    makeRemoteCompanies().create(makeCompany(values));

  const uploadCertificate = async (
    values: any,
    companyId: number,
  ): Promise<void> => {
    if (!hasCertificate(values)) return;

    const alreadyHasCertificate = Boolean(
      solicitation?.developerCompany?.company?.certificate,
    );

    const certificate = makeCertificate(values, String(companyId));

    if (!alreadyHasCertificate)
      await makeRemoteCompanies().createCertificate(certificate);

    await makeRemoteCompanies().updateCertificate(certificate);
  };

  const verifyDataToCreate = (values: any): boolean => {
    const requiredFields = [
      "companyCity",
      "companyNeighborhood",
      "companyNumber",
      "companyStreet",
      "companyState",
      "companyCep",
      "companyPhone",
      "especialTaxType",
      "fantasyName",
      "socialReason",
      "companyEmail",
      "municipalRegistration",
      "cnpj",
      "taxType",
    ];

    const isMissingFields = requiredFields.some(
      (field) =>
        values[field] === undefined ||
        values[field] === null ||
        values[field] === "",
    );

    return isMissingFields;
  };

  const createDeveloperCompany = (companyId: number): Promise<any> =>
    makeRemoteDeveloperCompanies().create({
      company: { id: companyId },
      developer: { id: solicitation?.developer?.id },
      solicitation: { id: solicitation?.id as number },
    });

  const updateSolicitationWithDeveloperCompany = (
    developerCompany: any,
  ): void => {
    setSolicitation(
      (prev) =>
        ({
          ...prev,
          developerCompany,
        } as ISolicitation),
    );
  };

  const handleExistingCompanyUpdate = async (data: any): Promise<void> => {
    const companyId = solicitation?.developerCompany?.company?.id || 0;
    await uploadCertificate(data, companyId);
    await updateCompany(data);
  };

  const handleNewCompanyRegistration = async (data: any): Promise<void> => {
    const company = await createCompany(data);
    const developerCompany = await createDeveloperCompany(company.id as number);

    updateSolicitationWithDeveloperCompany(developerCompany);
    await uploadCertificate(data, company.id as number);
  };

  const handleBasicRegister = async (data: any): Promise<void> => {
    const isNewCompanyRegistration =
      !solicitation?.developerCompany && !verifyDataToCreate(data);
    const isExistingCompanyUpdate = !!solicitation?.developerCompany;

    if (isNewCompanyRegistration) {
      await handleNewCompanyRegistration(data);
    } else if (isExistingCompanyUpdate) {
      await handleExistingCompanyUpdate(data);
    }
  };

  const handleSubmissionError = (error: any): void => {
    const errorMsg = getErrorMessage(error);
    setErrorMessage(errorMsg);
    showErrorMessage(errorMsg);
  };

  const handleSubmit = async (data: any): Promise<void> => {
    try {
      await updatePlan(data);

      switch (solicitation?.type) {
        case "COMPANY_REGISTER":
          await handleExistingCompanyUpdate(data);
          break;

        case "BASIC_REGISTER":
          await handleBasicRegister(data);
          break;

        default:
          throw new Error("Tipo de solicitação desconhecido");
      }

      showSuccessMessage("Solicitação atualizada com sucesso!");

      history.push("/admin/solicitations");
    } catch (error: any) {
      handleSubmissionError(error);
    }
  };

  const goBack = (): void => {
    history.goBack();
  };

  const concatenatePhoneNumbers = (): string => {
    const ddd = solicitation?.developerCompany?.company?.phone?.ddd ?? "";
    const number = solicitation?.developerCompany?.company?.phone?.number ?? "";

    return ddd + number;
  };

  const dataForm = [
    {
      items: [
        {
          name: "title",
          component: "Title",
          title: "Dados CNPJ: ",
          number: 1,
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "2fr 1fr" },
          inputs: [
            {
              name: "socialReason",
              component: "Input",
              label: "Razão Social",
              title: "Razão Social",
              value:
                solicitation?.developerCompany?.company?.socialReason || "",
            },
            {
              component: "InputMask",
              mask: "CNPJ",
              name: "cnpj",
              label: "CNPJ",
              title: "CNPJ",
              value:
                solicitation?.developerCompany?.company?.subscriptionNumber ||
                "",
              onChange: (data: any, el: any) =>
                  handleCnpjChange(data, el),
              validations: "cnpjValidationNotRequired",
            },
          ],
        },
        {
          component: "InputGroup",
          style: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            alignItems: "center",
          },
          inputs: [
            {
              name: "stateRegistration",
              component: "Input",
              label: "Inscrição Estadual",
              title: "Inscrição Estadual",
              value: solicitation?.developerCompany?.company?.stateRegistration,
            },
            {
              name: "municipalRegistration",
              component: "Input",
              label: "Inscrição Municipal",
              title: "Inscrição Municipal",
              value:
                solicitation?.developerCompany?.company?.municipalRegistration,
            },
            {
              name: "isMei",
              component: "InputCheck",
              title: "",
              label: "",
              checked: solicitation?.developerCompany?.company?.isMei || false,
              options: [
                {
                  value: "true",
                  label: "O CNPJ é MEI?",
                  name: "O CNPJ é MEI?",
                },
              ],
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "2fr 1fr" },
          inputs: [
            {
              name: "fantasyName",
              component: "Input",
              label: "Nome Fantasia",
              title: "Nome Fantasia",
              value: solicitation?.developerCompany?.company?.fantasyName || "",
            },
            {
              component: "Input",
              name: "companyEmail",
              label: "E-mail Cadastrado",
              title: "E-mail Cadastrado",
              value: solicitation?.developerCompany?.company?.email || "",
              validations: "validEmail",
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr" },
          inputs: [
            {
              name: "companyPhone",
              component: "InputMask",
              mask: "PHONE_NUMBER",
              label: "Telefone",
              title: "Telefone",
              value: concatenatePhoneNumbers(),
            },
            {
              name: "taxType",
              component: "Select",
              label: "Regime Tributário",
              title: "Regime Tributário",
              value: solicitation?.developerCompany?.company?.taxType || "",
              options: taxTypeList.map((tax) => ({
                value: tax.ENUM,
                label: tax.label,
              })),
            },
            {
              name: "especialTaxType",
              component: "Select",
              label: "Regime Tributário Especial",
              title: "Regime Tributário Especial",
              value:
                solicitation?.developerCompany?.company?.especialTaxType || especialTaxTypeList.find((item) => item.value === 9)?.ENUM,
              options: especialTaxTypeList.map((tax) => ({
                value: tax.ENUM,
                label: tax.label,
              })),
            },
          ],
        },
        {
          component: "InputGroup",
          hasDivider: true,
          style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr" },
          inputs: [
            {
              name: "simplesNacional",
              component: "InputCheck",
              title: "",
              label: "",
              checked:
                solicitation?.developerCompany?.company?.simpleNacional ||
                false,
              options: [
                {
                  value: "true",
                  label: "Optante pelo Simples Nacional",
                  name: "Optante pelo Simples Nacional",
                },
              ],
            },
            {
              name: "culturalIncentive",
              component: "InputCheck",
              title: "",
              label: "",
              checked:
                solicitation?.developerCompany?.company?.culturalIncentive ||
                false,
              options: [
                {
                  value: "true",
                  label: "Incentivo a Cultura",
                  name: "Incentivo a Cultura",
                },
              ],
            },
            {
              name: "taxIncentive",
              component: "InputCheck",
              title: "",
              label: "",
              checked:
                solicitation?.developerCompany?.company?.taxIncentive || false,
              options: [
                {
                  value: "true",
                  label: "Taxa de incentivo",
                  name: "Taxa de incentivo",
                },
              ],
            },
          ],
        },
        {
          name: "title",
          component: "Title",
          title: "Certificado Digital: ",
          number: 2,
        },
        {
          component: "InputGroup",
          style: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
          },
          hasDivider: true,
          inputs: [
            {
              name: "certificateFile",
              component: "InputUpload",
              label: "Certificado Digital",
              title: "Certificado Digital",
              accept: ".pfx",
              hasPreview: Boolean(
                solicitation?.developerCompany?.company?.certificate,
              ),
              url: `${settings.ASSETS_URL}/${solicitation?.developerCompany?.company?.certificate?.url}`,
            },
            {
              name: "certificatePassword",
              component: "InputPassword",
              type: "password",
              label: "Senha do Certificado",
              title: "Senha do Certificado",
              value:
                solicitation?.developerCompany?.company?.certificate
                  ?.password || "",
            },
            {
              name: "certificateEmail",
              component: "Input",
              label: "E-mail Cadastrado",
              title: "E-mail Cadastrado",
              value:
                solicitation?.developerCompany?.company?.certificate?.email ||
                "",
              validations: "validEmail",
            },
          ],
        },
        {
          name: "title",
          component: "Title",
          title: "Endereço do CNPJ: ",
          number: 3,
        },
        {
          component: "InputGroup",
          style: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
          },
          inputs: [
            {
              name: "companyCep",
              component: "InputMask",
              mask: "CEP",
              label: "CEP",
              title: "CEP",
              value:
                solicitation?.developerCompany?.company?.address?.zipCode || "",
              onChange: (data: any, el: any) =>
                handleCepChange(data, el, "company"),
            },
            {
              name: "tipoLogradouro",
              component: "Select",
              label: "Tipo de Logradouro",
              title: "Tipo de Logradouro",
              value:
                solicitation?.developerCompany?.company?.address
                  ?.tipoLogradouro || "",
              required: true,
              validations: "inputRequired",
              options: addressTypeList.map((address) => ({
                value: address.ENUM,
                label: address.label,
              })),
            },
            {
              component: "Input",
              name: "companyStreet",
              label: "Rua",
              title: "Rua",
              value:
                solicitation?.developerCompany?.company?.address?.place || "",
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr" },
          inputs: [
            {
              name: "companyNumber",
              component: "Input",
              label: "Número",
              title: "Número",
              value:
                solicitation?.developerCompany?.company?.address?.number || "",
            },
            {
              name: "companyNeighborhood",
              component: "Input",
              label: "Bairro",
              title: "Bairro",
              value:
                solicitation?.developerCompany?.company?.address?.district ||
                "",
            },
            {
              name: "companyComplement",
              component: "Input",
              label: "Complemento",
              title: "Complemento",
              value:
                solicitation?.developerCompany?.company?.address?.complement ||
                "",
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr" },
          hasDivider: true,
          inputs: [
            {
              name: "companyCity",
              component: "Input",
              label: "Cidade",
              title: "Cidade",
              value:
                solicitation?.developerCompany?.company?.address?.city || "",
            },
            {
              name: "companyState",
              component: "Input",
              label: "Estado",
              title: "Estado",
              value:
                solicitation?.developerCompany?.company?.address?.state || "",
            },
          ],
        },
        {
          name: "title",
          component: "Title",
          title: "Plano: ",
          number: 4,
        },
        {
          component: "InputGroup",
          style: {
            display: "grid",
            gridTemplateColumns: isSmallScreen ? "1fr 1fr" : "1fr 1fr 1fr",
          },
          hasDivider: true,
          inputs: [
            {
              name: "plan",
              component: "Select",
              label: "Plano",
              title: "Plano",
              value: solicitation?.developerPackage.pack?.id || "",
              options: packages.map((pack) => ({
                value: pack.id,
                label: pack.packageEnum,
              })),
            },
            {
              name: "planStatus",
              component: "Select",
              label: "Status",
              title: "Status",
              value: solicitation?.developerPackage.status || "",
              options: planStatusList,
            },
          ],
        },
        {
          component: "InputGroup",
          style: { display: "flex", gap: "16px", width: "100%", marginTop: 16 },
          inputs: [
            {
              name: "backButton",
              component: "Button",
              type: "button",
              title: "Voltar",
              onClick: goBack,
              color: false,
            },
            {
              name: "button",
              component: "Button",
              title: "Enviar",
              submittingText: "Carregando...",
              color: true,
            },
          ],
        },
      ],
    },
  ];

  const props = {
    dataForm,
    onSubmit: handleSubmit,
    errorMessage,
  };
  return <SolicitationEdit {...props} />;
};

export default MakeSolicitationEdit;
